import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import HelpHint from './HelpHint';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Plus,
  Edit,
  Trash2,
  Link,
  Webhook,
  Settings,
  CheckCircle,
  XCircle,
  RefreshCw,
  Send,
  Copy,
  Eye,
  EyeOff
} from 'lucide-react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import SectionIntro from './SectionIntro';
import { XpnsrTableSkeleton } from './XpnsrSkeleton';
import XpnsrEmpty from './XpnsrEmpty';

export default function Integrations() {
  const { user } = useAuth();
  const { t } = useT();

  const INTEGRATION_TYPES = [
    { value: 'trackbox', label: 'TrackBox / Tigloo', description: t('Affiliate CRM — push leads via API') },
    { value: 'openaff', label: 'OpenAFF', description: t('Affiliate network — leads via API and status sync') },
    { value: 'api', label: t('Custom API (any CRM)'), description: t('Configure any partner CRM lead API') },
  ];

  const WEBHOOK_EVENTS = [
    { value: 'click', label: t('Click') },
    { value: 'conversion', label: t('Conversion') },
    { value: 'campaign_paused', label: t('Campaign paused') },
    { value: 'campaign_started', label: t('Campaign started') },
    { value: 'fraud_detected', label: t('Fraud detected') },
    { value: 'threshold_reached', label: t('Threshold reached') }
  ];

  const [activeTab, setActiveTab] = useState('integrations');
  const [integrations, setIntegrations] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Lead-integration catalogue (GET /lead-integration-types). null = catalogue
  // unavailable (endpoint failed / older backend) → the static per-type forms
  // below keep working exactly as before.
  const [leadTypes, setLeadTypes] = useState(null);

  const [createIntDialogOpen, setCreateIntDialogOpen] = useState(false);
  const [editIntDialogOpen, setEditIntDialogOpen] = useState(false);
  const [editingInt, setEditingInt] = useState(null);

  const [createWHDialogOpen, setCreateWHDialogOpen] = useState(false);
  const [editWHDialogOpen, setEditWHDialogOpen] = useState(false);
  const [editingWH, setEditingWH] = useState(null);

  const [intForm, setIntForm] = useState({ type: '', name: '', config: {} });
  const [whForm, setWhForm] = useState({ name: '', url: '', events: [], secret: '', is_active: true, retry_attempts: 3, timeout_seconds: 30 });
  const [configField, setConfigField] = useState({ key: '', value: '' });
  const [showSecrets, setShowSecrets] = useState({});

  // Test integration: fire a synthetic lead at the CRM and show the response.
  const [testTarget, setTestTarget] = useState(null);
  const [testForm, setTestForm] = useState({ gi: '', email: '', phone: '' });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // TrackBox Pull sync: pull statuses/deposits and update stored leads.
  const [pullTarget, setPullTarget] = useState(null);
  const [pullResult, setPullResult] = useState(null);
  const [pulling, setPulling] = useState(false);

  const openPull = (item) => {
    setPullTarget(item);
    setPullResult(null);
  };

  const runPull = async () => {
    if (!pullTarget) return;
    setPulling(true);
    setPullResult(null);
    try {
      // OpenAFF syncs the number of days set on the integration (one request per day).
      const body = pullTarget.type === 'openaff' ? {} : { days: 7 };
      const res = await axios.post(API + '/integrations/' + pullTarget.id + '/pull-sync', body);
      setPullResult(res.data);
      toast.success(t('Pull sync done') + ': ' + (res.data && res.data.updated != null ? res.data.updated : 0) + '/' + (res.data && res.data.matched != null ? res.data.matched : 0));
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || 'request failed';
      setPullResult({ error: detail });
      toast.error(detail);
    } finally {
      setPulling(false);
    }
  };

  const openTest = (item) => {
    setTestTarget(item);
    setTestForm({ gi: '', email: '', phone: '', ip: '', country: '' });
    setTestResult(null);
  };

  const runTest = async () => {
    if (!testTarget) return;
    setTesting(true);
    setTestResult(null);
    try {
      const params = {};
      if (testForm.gi.trim()) params.gi = testForm.gi.trim();
      const lead = {};
      if (testForm.email.trim()) lead.email = testForm.email.trim();
      if (testForm.phone.trim()) lead.phone = testForm.phone.trim();
      if ((testForm.ip || '').trim()) lead.userip = testForm.ip.trim();
      if ((testForm.country || '').trim()) lead.country = testForm.country.trim().toUpperCase();
      const res = await axios.post(API + '/integrations/' + testTarget.id + '/test', { params, lead });
      setTestResult(res.data);
      if (res.data && res.data.ok) toast.success(t('Test lead accepted'));
      else toast.error(t('Test lead not accepted'));
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || 'request failed';
      setTestResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchIntegrations(), fetchWebhooks(), fetchLeadTypes()]);
    setLoading(false);
  };

  const fetchLeadTypes = async () => {
    try {
      const res = await axios.get(`${API}/lead-integration-types`);
      const types = res.data && Array.isArray(res.data.types) ? res.data.types : [];
      if (types.length) setLeadTypes(types);
    } catch (e) {
      // Degrade silently to the static type list / forms.
    }
  };

  const fetchIntegrations = async () => {
    try {
      const res = await axios.get(`${API}/integrations`);
      setIntegrations(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const res = await axios.get(`${API}/webhooks`);
      setWebhooks(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  // Required-field validation against the catalogue (no-op when the catalogue
  // is unavailable — matches the pre-catalogue behavior).
  const missingRequiredConfig = () => {
    const fields = catalogueConfigFields(intForm.type) || [];
    return fields
      .filter(f => f.required && !String(intForm.config[f.key] || '').trim())
      .map(f => t(f.label || f.key));
  };

  // Integration CRUD
  const handleCreateInt = async () => {
    if (!intForm.type || !intForm.name) { toast.error(t('Fill in type and name')); return; }
    const missing = missingRequiredConfig();
    if (missing.length) { toast.error(t('Missing fields') + ': ' + missing.join(', ')); return; }
    try {
      const res = await axios.post(`${API}/integrations`, intForm);
      setIntegrations([res.data, ...integrations]);
      setCreateIntDialogOpen(false);
      resetIntForm();
      toast.success(t('Integration created'));
    } catch (e) {
      toast.error(t('Error creating'));
    }
  };

  const handleUpdateInt = async () => {
    if (!editingInt) return;
    const missing = missingRequiredConfig();
    if (missing.length) { toast.error(t('Missing fields') + ': ' + missing.join(', ')); return; }
    try {
      const res = await axios.put(`${API}/integrations/${editingInt.id}`, {
        name: intForm.name, config: intForm.config, status: intForm.status
      });
      setIntegrations(integrations.map(i => i.id === editingInt.id ? res.data : i));
      setEditIntDialogOpen(false);
      setEditingInt(null);
      toast.success(t('Integration updated'));
    } catch (e) {
      toast.error(t('Error updating'));
    }
  };

  const [deleteIntTarget, setDeleteIntTarget] = useState(null);
  const [deleteWHTarget, setDeleteWHTarget] = useState(null);

  const handleDeleteInt = async (id) => {
    try {
      await axios.delete(`${API}/integrations/${id}`);
      setIntegrations(integrations.filter(i => i.id !== id));
      setDeleteIntTarget(null);
      toast.success(t('Integration deleted'));
    } catch (e) {
      toast.error(t('Error deleting'));
    }
  };

  const setCfg = (k, v) => setIntForm(prev => ({ ...prev, config: { ...prev.config, [k]: v } }));

  const resetIntForm = () => {
    setIntForm({ type: '', name: '', config: {} });
    setConfigField({ key: '', value: '' });
  };

  const addConfigField = () => {
    if (configField.key) {
      setIntForm(prev => ({ ...prev, config: { ...prev.config, [configField.key]: configField.value } }));
      setConfigField({ key: '', value: '' });
    }
  };

  const removeConfigField = (key) => {
    setIntForm(prev => {
      const cfg = { ...prev.config };
      delete cfg[key];
      return { ...prev, config: cfg };
    });
  };

  const openEditInt = (item) => {
    setEditingInt(item);
    setIntForm({ type: item.type, name: item.name, config: item.config || {}, status: item.status });
    setEditIntDialogOpen(true);
  };

  // Webhook CRUD
  const handleCreateWH = async () => {
    if (!whForm.name || !whForm.url) { toast.error(t('Fill in name and URL')); return; }
    try {
      const res = await axios.post(`${API}/webhooks`, whForm);
      setWebhooks([res.data, ...webhooks]);
      setCreateWHDialogOpen(false);
      resetWHForm();
      toast.success(t('Webhook created'));
    } catch (e) {
      toast.error(t('Error creating webhook'));
    }
  };

  const handleUpdateWH = async () => {
    if (!editingWH) return;
    try {
      const res = await axios.put(`${API}/webhooks/${editingWH.id}`, whForm);
      setWebhooks(webhooks.map(w => w.id === editingWH.id ? res.data : w));
      setEditWHDialogOpen(false);
      setEditingWH(null);
      toast.success(t('Webhook updated'));
    } catch (e) {
      toast.error(t('Error updating'));
    }
  };

  const handleDeleteWH = async (id) => {
    try {
      await axios.delete(`${API}/webhooks/${id}`);
      setWebhooks(webhooks.filter(w => w.id !== id));
      setDeleteWHTarget(null);
      toast.success(t('Webhook deleted'));
    } catch (e) {
      toast.error(t('Error deleting'));
    }
  };

  const handleTestWH = async (id) => {
    try {
      const res = await axios.post(`${API}/webhooks/${id}/test`);
      if (res.data.status === 'ok') {
        toast.success(t('Test succeeded (HTTP {code})', { code: res.data.status_code }));
      } else {
        toast.error(t('Test error: {detail}', { detail: res.data.detail || res.data.status_code }));
      }
      fetchWebhooks();
    } catch (e) {
      toast.error(t('Error testing webhook'));
    }
  };

  const resetWHForm = () => {
    setWhForm({ name: '', url: '', events: [], secret: '', is_active: true, retry_attempts: 3, timeout_seconds: 30 });
  };

  const openEditWH = (item) => {
    setEditingWH(item);
    setWhForm({
      name: item.name, url: item.url, events: item.events || [],
      secret: item.secret || '', is_active: item.is_active,
      retry_attempts: item.retry_attempts || 3, timeout_seconds: item.timeout_seconds || 30
    });
    setEditWHDialogOpen(true);
  };

  const toggleWHEvent = (eventValue) => {
    setWhForm(prev => ({
      ...prev,
      events: prev.events.includes(eventValue)
        ? prev.events.filter(e => e !== eventValue)
        : [...prev.events, eventValue]
    }));
  };

  // Type options come from the backend catalogue when it loaded; otherwise the
  // static INTEGRATION_TYPES list keeps the form usable (backward compatible).
  const typeOptions = (leadTypes && leadTypes.length)
    ? leadTypes.map(ti => {
        const staticInfo = INTEGRATION_TYPES.find(s => s.value === ti.type);
        return {
          value: ti.type,
          label: ti.label || (staticInfo ? staticInfo.label : ti.type),
          description: staticInfo ? staticInfo.description : '',
        };
      })
    : INTEGRATION_TYPES;

  const getIntTypeInfo = (type) => typeOptions.find(ti => ti.value === type) || INTEGRATION_TYPES.find(ti => ti.value === type) || { label: type, description: '' };

  // Catalogue config fields for the currently selected integration type.
  // Returns null when the catalogue is unavailable or the type declares no
  // config fields — the static per-type form is used instead.
  const catalogueConfigFields = (type) => {
    const ct = (leadTypes || []).find(ti => ti.type === type);
    return ct && Array.isArray(ct.config_fields) && ct.config_fields.length ? ct.config_fields : null;
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6" data-testid="integrations-skeleton">
        <XpnsrPageHeader kicker={t('Connections')} title={t('Integrations')} subtitle={t('Connect external services and webhooks')} />
        <XpnsrTableSkeleton rows={4} columns={4} />
      </div>
    );
  }

  const IntFormFields = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Integration type')} <HelpHint text={t("Ready-made adapters are for partner CRMs with a known API: you only fill in the account fields. Custom API is for any other CRM: you configure the request yourself (URL, headers, body, where to read the redirect).")} /></Label>
          {/* Type is immutable after creation: the PUT endpoint (and IntegrationUpdate)
              don't accept `type`, and each type has its own config shape — letting the
              select change it here silently dropped the change. Disable when editing. */}
          <Select value={intForm.type} disabled={!!editingInt} onValueChange={(v) => setIntForm(prev => ({ ...prev, type: v, name: prev.name || getIntTypeInfo(v).label }))}>
            <SelectTrigger data-testid="int-type-select" disabled={!!editingInt}><SelectValue placeholder={t('Select type')}>{intForm.type ? getIntTypeInfo(intForm.type).label : null}</SelectValue></SelectTrigger>
            <SelectContent>
              {typeOptions.map(ti => (
                <SelectItem key={ti.value} value={ti.value}>
                  <div>
                    <span className="font-medium">{ti.label}</span>
                    <span className="text-xs text-muted-foreground ml-2">{ti.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input data-testid="int-name-input" value={intForm.name} onChange={(e) => setIntForm(prev => ({ ...prev, name: e.target.value }))} placeholder={t('Name')} />
        </div>
      </div>
      {catalogueConfigFields(intForm.type) ? (
        // Catalogue-driven form: the backend declares which connection fields
        // this partner type needs (label / required / secret / default).
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('Credentials and settings for this partner. Fields marked * are required.')}</p>
          <div className="grid grid-cols-2 gap-3">
            {catalogueConfigFields(intForm.type).map(f => (
              <div key={f.key} className="space-y-1">
                <Label>{t(f.label || f.key)}{f.required ? <span className="text-destructive"> *</span> : null}{f.hint ? <> <HelpHint text={t(f.hint)} /></> : null}</Label>
                {f.type === 'switch' ? (
                  <div className="flex items-center gap-2 h-9">
                    <Switch
                      checked={['1', 'true', 'yes', 'on'].includes(String(intForm.config[f.key] || '').toLowerCase())}
                      onCheckedChange={(v) => setCfg(f.key, v ? '1' : '')}
                      aria-label={t(f.label || f.key)}
                    />
                  </div>
                ) : (
                  <Input
                    type={f.secret ? 'password' : 'text'}
                    autoComplete={f.secret ? 'new-password' : 'off'}
                    placeholder={f.default != null ? String(f.default) : ''}
                    value={intForm.config[f.key] || ''}
                    onChange={(e) => setCfg(f.key, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ) : intForm.type === 'trackbox' ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('TrackBox account credentials (dashboard → API Testing). Set up once per account — offers will reference this partner.')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>API URL</Label><Input placeholder="tb.yourpartner.pro" value={intForm.config.base_url || ''} onChange={(e) => setCfg('base_url', e.target.value)} /></div>
            <div className="space-y-1"><Label>x-api-key <HelpHint text={t("The partner's secret Push/Registration key. Get it in the partner dashboard (API / API Testing section) or from your affiliate manager.")} /></Label><Input placeholder="x-api-key" value={intForm.config.api_key || ''} onChange={(e) => setCfg('api_key', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Username')}</Label><Input placeholder="x-trackbox-username" value={intForm.config.username || ''} onChange={(e) => setCfg('username', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Password')}</Label><Input placeholder="x-trackbox-password" value={intForm.config.password || ''} onChange={(e) => setCfg('password', e.target.value)} /></div>
            <div className="space-y-1"><Label>Affiliate ID (ai) <HelpHint text={t("Your Affiliate ID at the partner. Provided by the partner.")} /></Label><Input placeholder="2958445" value={intForm.config.ai || ''} onChange={(e) => setCfg('ai', e.target.value)} /></div>
            <div className="space-y-1"><Label>Campaign ID (ci) <HelpHint text={t("Campaign ID at the partner. Usually 1 unless told otherwise.")} /></Label><Input placeholder="1" value={intForm.config.ci || ''} onChange={(e) => setCfg('ci', e.target.value)} /></div>
            <div className="space-y-1"><Label>Redirect id (rd)</Label><Input placeholder="94 (optional)" value={intForm.config.rd || ''} onChange={(e) => setCfg('rd', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Signup path')}</Label><Input placeholder="/api/signup/procform" value={intForm.config.signup_path || ''} onChange={(e) => setCfg('signup_path', e.target.value)} /></div>
            <div className="space-y-1"><Label>Pull x-api-key (2nd key)</Label><Input placeholder="pull x-api-key" value={intForm.config.pull_api_key || ''} onChange={(e) => setCfg('pull_api_key', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Pull path')}</Label><Input placeholder="/api/pull/customers" value={intForm.config.pull_path || ''} onChange={(e) => setCfg('pull_path', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Pull type')}</Label><Input placeholder="3 (2=leads, 3=+deposits, 4=deposits)" value={intForm.config.pull_type || ''} onChange={(e) => setCfg('pull_type', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Auto pull-sync')}</Label><div className="flex items-center gap-2 h-9"><Switch checked={['1','true','yes','on'].includes(String(intForm.config.pull_enabled || '').toLowerCase())} onCheckedChange={(v) => setCfg('pull_enabled', v ? 'true' : '')} /><span className="text-xs text-muted-foreground">{t('statuses on schedule')}</span></div></div>
          </div>
        </div>
      ) : intForm.type === 'api' ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('Universal integration for any partner CRM. Describe its lead-registration API request — the tracker will send form data there and redirect using the link from the response.')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{t('Method')}</Label>
              <Select value={intForm.config.method || 'POST'} onValueChange={(v) => setCfg('method', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="POST">POST</SelectItem><SelectItem value="GET">GET</SelectItem><SelectItem value="PUT">PUT</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>{t('API URL (base)')}</Label><Input placeholder="https://crm.partner.com" value={intForm.config.base_url || ''} onChange={(e) => setCfg('base_url', e.target.value)} /></div>
            <div className="space-y-1">
              <Label>{t('Body format')}</Label>
              <Select value={intForm.config.content_type || 'json'} onValueChange={(v) => setCfg('content_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="json">JSON</SelectItem><SelectItem value="form">form-urlencoded</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1"><Label>{t('Signup path')}</Label><Input placeholder="/api/register" value={intForm.config.signup_path || ''} onChange={(e) => setCfg('signup_path', e.target.value)} /></div>
          <div className="space-y-1">
            <Label>{t('Headers')} <span className="text-muted-foreground font-normal">{t('(one "Key: value" per line, macros allowed)')}</span></Label>
            <textarea className="w-full min-h-[64px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" placeholder="x-api-key: {api_key}" value={intForm.config.headers || ''} onChange={(e) => setCfg('headers', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>{t('Body template (JSON)')} <span className="text-muted-foreground font-normal">{t('(values are macros in curly braces)')}</span></Label>
            <textarea className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" placeholder={'{ "email": "{email}", "firstname": "{firstname}", "phone": "{phone}", "gi": "{gi}", "sub": "{click_id}" }'} value={intForm.config.body_template || ''} onChange={(e) => setCfg('body_template', e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1"><Label>{t('Redirect field')}</Label><Input placeholder="data.url" value={intForm.config.redirect_path || ''} onChange={(e) => setCfg('redirect_path', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Success field (optional)')}</Label><Input placeholder="status" value={intForm.config.success_path || ''} onChange={(e) => setCfg('success_path', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Success values (optional, comma-separated)')}</Label><Input placeholder="true, ok, success" value={intForm.config.success_value || ''} onChange={(e) => setCfg('success_value', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1"><Label>{t('Basic auth (user:password, optional)')}</Label><Input type="password" autoComplete="off" placeholder="user:password" value={intForm.config.basic_auth || ''} onChange={(e) => setCfg('basic_auth', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Lead ID field (optional)')}</Label><Input placeholder="data.lead_id" value={intForm.config.lead_id_path || ''} onChange={(e) => setCfg('lead_id_path', e.target.value)} /></div>
            <div className="space-y-1"><Label>{t('Error message field (optional)')}</Label><Input placeholder="errors" value={intForm.config.message_path || ''} onChange={(e) => setCfg('message_path', e.target.value)} /></div>
          </div>
          <div className="rounded-md border border-border bg-surface-2 p-2 text-[0.7rem] text-muted-foreground space-y-1">
            <div>{t('Available macros')} — {t('plus any form fields and offer parameters. The redirect field is a path in the JSON response (e.g. data.url); if empty, the first http(s) link found in the response is used.')}</div>
            {[
              ["Lead data", "{firstname} {lastname} {first_name} {last_name} {full_name} {email} {password} {password_symbol}"],
              ["Phone number", "{phone} {phone_plus} {phone_e164} {phone_cc} {phone_national} {phone_digits}"],
              ["Geo and language", "{country} {country_iso2} {country_iso3} {country_lower} {country_name} {city} {region} {timezone} {lang} {lang_upper} {locale}"],
              ["Visitor", "{ip} {ipv4} {userip} {user_agent} {accept_language} {referer} {page_url} {domain} {device_type} {os} {browser} {isp}"],
              ["Click and campaign", "{click_id} {sub} {campaign_id} {campaign_name} {stream_id} {stream_name} {gclid} {fbclid} {ttclid} {utm_source}… {sub1}…{sub10}"],
              ["Time and IDs", "{uuid} {timestamp} {date} {datetime}"],
            ].map(([g, m]) => (
              <div key={g}><span className="text-foreground">{t(g)}:</span> <span className="font-mono">{m}</span></div>
            ))}
            <div><span className="text-foreground">{t('Filters')}:</span> <span className="font-mono">{"{name|lower} {name|upper} {name|trim} {name|digits} {name|default:value} {name|prefix:+} {name|limit:100} {name|int} {name|number} {email|sha256} {email|md5} {name|base64} {name|urlencode} {date|format:DD.MM.YYYY}"}</span></div>
            <div>{t('Macros also work in the signup path; their values are URL-encoded there. The password is taken from the form or generated (letters and digits).')} <RouterLink to="/docs#section-23-step-1" className="text-primary hover:underline">{t('Open the guide')}</RouterLink></div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label>{t('Configuration (key-value)')}</Label>
          <div className="flex gap-2">
            <Input placeholder={t('Key (e.g. api_key)')} value={configField.key} onChange={(e) => setConfigField(prev => ({ ...prev, key: e.target.value }))} />
            <Input placeholder={t('Value')} value={configField.value} onChange={(e) => setConfigField(prev => ({ ...prev, value: e.target.value }))} />
            <Button type="button" variant="outline" onClick={addConfigField}>+</Button>
          </div>
          {Object.entries(intForm.config).length > 0 && (
            <div className="space-y-1 mt-2">
              {Object.entries(intForm.config).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 bg-surface-2 p-2 rounded text-sm">
                  <span className="font-mono text-muted-foreground">{k}:</span>
                  <span className="font-mono truncate">{v}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 ml-auto" aria-label={t('Remove field')} title={t('Remove field')} onClick={() => removeConfigField(k)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const WHFormFields = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input data-testid="wh-name-input" value={whForm.name} onChange={(e) => setWhForm(prev => ({ ...prev, name: e.target.value }))} placeholder={t('My webhook')} />
        </div>
        <div className="space-y-2">
          <Label>{t('Secret (optional)')}</Label>
          <Input value={whForm.secret} onChange={(e) => setWhForm(prev => ({ ...prev, secret: e.target.value }))} placeholder="webhook_secret_key" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>URL</Label>
        <Input data-testid="wh-url-input" value={whForm.url} onChange={(e) => setWhForm(prev => ({ ...prev, url: e.target.value }))} placeholder="https://example.com/webhook" />
      </div>
      <div className="space-y-2">
        <Label>{t('Events')}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {WEBHOOK_EVENTS.map(ev => (
            <label key={ev.value} className="flex items-center gap-2 p-2 border border-border rounded cursor-pointer hover:bg-surface-2">
              <input
                type="checkbox"
                checked={whForm.events.includes(ev.value)}
                onChange={() => toggleWHEvent(ev.value)}
                className="rounded"
              />
              <span className="text-sm">{ev.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Retry attempts')}</Label>
          <Input type="number" value={whForm.retry_attempts} onChange={(e) => setWhForm(prev => ({ ...prev, retry_attempts: parseInt(e.target.value) || 0 }))} min="0" max="10" />
        </div>
        <div className="space-y-2">
          <Label>{t('Timeout (sec)')}</Label>
          <Input type="number" value={whForm.timeout_seconds} onChange={(e) => setWhForm(prev => ({ ...prev, timeout_seconds: parseInt(e.target.value) || 10 }))} min="1" max="120" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8 space-y-6" data-testid="integrations-view">
      <XpnsrPageHeader
        kicker={t('Connections')}
        title={t('Integrations')}
        subtitle={t('Connect external services and webhooks to automate workflows.')}
      />

      <SectionIntro>
        {t('Set up a partner/CRM here once, then offers reference it. Pick a ready-made adapter if the partner is in the list, otherwise Custom API. Leads from offer forms are sent to the selected partner.')} <RouterLink to="/docs#section-23-step-1" className="text-primary hover:underline">{t('Open the guide')}</RouterLink>
      </SectionIntro>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="integrations">{t('Integrations')} ({integrations.length})</TabsTrigger>
          <TabsTrigger value="webhooks">{t('Webhooks')} ({webhooks.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="integrations" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={createIntDialogOpen} onOpenChange={(open) => { setCreateIntDialogOpen(open); if (!open) resetIntForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="create-integration-btn" onClick={resetIntForm}>
                  <Plus className="w-4 h-4 mr-2" />{t('Add integration')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>{t('New integration')}</DialogTitle>
                  <DialogDescription>{t('Configure connection to an external service')}</DialogDescription>
                </DialogHeader>
                {IntFormFields()}
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setCreateIntDialogOpen(false)}>{t('Cancel')}</Button>
                  <Button data-testid="save-integration-btn" onClick={handleCreateInt}>{t('Create')}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {integrations.length === 0 ? (
            <Card>
              <CardContent style={{ padding: 0 }}>
                <XpnsrEmpty
                  icon={Link}
                  title={t('No integrations')}
                  subtitle={t('Connect external services to automate workflows')}
                  cta={
                    <button
                      type="button"
                      className="xp-btn xp-btn-primary"
                      onClick={() => setCreateIntDialogOpen(true)}
                      data-testid="empty-create-integration"
                    >
                      <Plus style={{ width: 12, height: 12 }} /> {t('New integration')}
                    </button>
                  }
                />
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {integrations.map(item => {
                const typeInfo = getIntTypeInfo(item.type);
                return (
                  <Card key={item.id} data-testid={`int-card-${item.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base">{item.name}</CardTitle>
                          <CardDescription>{typeInfo.description}</CardDescription>
                        </div>
                        <Badge variant={item.status === 'connected' ? 'default' : 'secondary'}>
                          {item.status === 'connected' ? <><CheckCircle className="w-3 h-3 mr-1" />{t('Connected')}</> : <><XCircle className="w-3 h-3 mr-1" />{t('Disconnected')}</>}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{typeInfo.label}</Badge>
                        <div className="flex gap-1">
                          {['trackbox', 'openaff'].includes(item.type) ? (
                            <Button variant="ghost" size="sm" onClick={() => openPull(item)} aria-label={t('Pull sync (statuses/deposits)')} title={t('Pull sync (statuses/deposits)')} data-testid={'int-pull-' + item.id}>
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          ) : null}
                          <Button variant="ghost" size="sm" onClick={() => openTest(item)} aria-label={t('Test integration')} title={t('Test integration')} data-testid={'int-test-' + item.id}>
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" aria-label={t('Edit integration')} title={t('Edit integration')} onClick={() => openEditInt(item)} data-testid={`int-edit-${item.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" aria-label={t('Delete integration')} title={t('Delete integration')} onClick={() => setDeleteIntTarget(item)} data-testid={`int-delete-${item.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      {item.last_sync && (
                        <p className="text-xs text-muted-foreground mt-2">{t('Last sync')}: {new Date(item.last_sync).toLocaleString()}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={createWHDialogOpen} onOpenChange={(open) => { setCreateWHDialogOpen(open); if (!open) resetWHForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="create-webhook-btn" onClick={resetWHForm}>
                  <Plus className="w-4 h-4 mr-2" />{t('Add webhook')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>{t('New webhook')}</DialogTitle>
                  <DialogDescription>{t('Configure a webhook to receive notifications')}</DialogDescription>
                </DialogHeader>
                {WHFormFields()}
                <div className="flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setCreateWHDialogOpen(false)}>{t('Cancel')}</Button>
                  <Button data-testid="save-webhook-btn" onClick={handleCreateWH}>{t('Create')}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {webhooks.length === 0 ? (
            <Card>
              <CardContent style={{ padding: 0 }}>
                <XpnsrEmpty
                  icon={Webhook}
                  title={t('No webhooks')}
                  subtitle={t('Create webhooks to send events to external systems')}
                  cta={
                    <button
                      type="button"
                      className="xp-btn xp-btn-primary"
                      onClick={() => setCreateWHDialogOpen(true)}
                      data-testid="empty-create-webhook"
                    >
                      <Plus style={{ width: 12, height: 12 }} /> {t('New webhook')}
                    </button>
                  }
                />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('Name')}</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>{t('Events')}</TableHead>
                    <TableHead className="text-center">{t('Success')}</TableHead>
                    <TableHead className="text-center">{t('Errors')}</TableHead>
                    <TableHead>{t('Status')}</TableHead>
                    <TableHead className="text-right">{t('Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map(wh => (
                    <TableRow key={wh.id} data-testid={`wh-row-${wh.id}`}>
                      <TableCell className="font-medium">{wh.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 max-w-[200px]">
                          <span className="truncate text-sm text-muted-foreground font-mono">{wh.url}</span>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" aria-label={t('Copy webhook URL')} title={t('Copy webhook URL')} onClick={() => { navigator.clipboard.writeText(wh.url); toast.success(t('URL copied')); }}>
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(wh.events || []).map(ev => (
                            <Badge key={ev} variant="outline" className="text-xs">
                              {WEBHOOK_EVENTS.find(e => e.value === ev)?.label || ev}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-primary font-medium">{wh.success_count || 0}</TableCell>
                      <TableCell className="text-center text-destructive font-medium">{wh.error_count || 0}</TableCell>
                      <TableCell>
                        <Badge variant={wh.is_active ? 'default' : 'secondary'}>
                          {wh.is_active ? t('Active') : t('Inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleTestWH(wh.id)} aria-label={t('Test')} title={t('Test')} data-testid={`wh-test-${wh.id}`}>
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" aria-label={t('Edit webhook')} title={t('Edit webhook')} onClick={() => openEditWH(wh)} data-testid={`wh-edit-${wh.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" aria-label={t('Delete webhook')} title={t('Delete webhook')} onClick={() => setDeleteWHTarget(wh)} data-testid={`wh-delete-${wh.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Integration Dialog */}
      <Dialog open={editIntDialogOpen} onOpenChange={(open) => { setEditIntDialogOpen(open); if (!open) { setEditingInt(null); resetIntForm(); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('Edit integration')}</DialogTitle>
          </DialogHeader>
          {IntFormFields()}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setEditIntDialogOpen(false)}>{t('Cancel')}</Button>
            <Button data-testid="update-integration-btn" onClick={handleUpdateInt}>{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Webhook Dialog */}
      <Dialog open={editWHDialogOpen} onOpenChange={(open) => { setEditWHDialogOpen(open); if (!open) { setEditingWH(null); resetWHForm(); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('Edit webhook')}</DialogTitle>
          </DialogHeader>
          {WHFormFields()}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setEditWHDialogOpen(false)}>{t('Cancel')}</Button>
            <Button data-testid="update-webhook-btn" onClick={handleUpdateWH}>{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!testTarget} onOpenChange={(open) => { if (!open) { setTestTarget(null); setTestResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('Test integration')}{testTarget ? ' - ' + testTarget.name : ''}</DialogTitle>
            <DialogDescription>{t('Send a synthetic lead to the CRM and inspect the response. A throwaway email/phone is used unless you override below.')}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {testTarget && ['trackbox', 'api'].includes(testTarget.type) ? (
              <div>
                <Label className="text-xs">gi</Label>
                <Input value={testForm.gi} onChange={(e) => setTestForm(f => ({ ...f, gi: e.target.value }))} placeholder={t('box/goal id')} />
              </div>
            ) : null}
            <div>
              <Label className="text-xs">{t('Visitor IP')} <HelpHint text={t("Optional. A regular IPv4 of the target GEO: lets you test from a VPN or an IPv6 network. Empty = your own IP.")} /></Label>
              <Input value={testForm.ip || ''} onChange={(e) => setTestForm(f => ({ ...f, ip: e.target.value }))} placeholder="auto" />
            </div>
            <div>
              <Label className="text-xs">{t('Country (ISO2)')} <HelpHint text={t("Optional. Without a phone, the test phone gets the code of this country.")} /></Label>
              <Input value={testForm.country || ''} onChange={(e) => setTestForm(f => ({ ...f, country: e.target.value }))} placeholder="auto" maxLength={2} />
            </div>
            <div>
              <Label className="text-xs">{t('Email')}</Label>
              <Input value={testForm.email} onChange={(e) => setTestForm(f => ({ ...f, email: e.target.value }))} placeholder="auto" />
            </div>
            <div>
              <Label className="text-xs">{t('Phone')}</Label>
              <Input value={testForm.phone} onChange={(e) => setTestForm(f => ({ ...f, phone: e.target.value }))} placeholder="auto" />
            </div>
          </div>
          {testResult ? (
            <div className="mt-2 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant={testResult.ok ? 'success' : 'destructive'}>{testResult.ok ? t('Accepted') : t('Not accepted')}</Badge>
                {testResult.http_status ? <span className="text-muted-foreground">HTTP {testResult.http_status}</span> : null}
              </div>
              {testResult.error ? <div className="text-destructive break-all">{testResult.error}</div> : null}
              {testResult.message ? <div className="text-destructive break-all">{t('Reason')}: {testResult.message}</div> : null}
              {!testResult.ok && testResult.http_status === 403 && testTarget && testTarget.type === 'openaff' ? (
                <div className="rounded-md border border-border bg-surface-2 p-2 text-xs">{t('OpenAFF rejected the visitor IP (VPN, data center or IPv6). Turn off the VPN or enter a visitor IP above.')}</div>
              ) : null}
              {testResult.crm_lead_id ? <div className="break-all"><span className="text-muted-foreground">{t('Partner lead ID')}: </span>{testResult.crm_lead_id}</div> : null}
              {testResult.redirect_url ? (
                <div className="break-all"><span className="text-muted-foreground">{t('Redirect')}: </span><a href={testResult.redirect_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{testResult.redirect_url}</a></div>
              ) : null}
              {testResult.request ? (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{t('Request')}</div>
                  <pre className="bg-surface-2 p-2 rounded text-xs overflow-auto max-h-40">{JSON.stringify(testResult.request, null, 2)}</pre>
                </div>
              ) : null}
              {testResult.response ? (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{t('CRM response')}</div>
                  <pre className="bg-surface-2 p-2 rounded text-xs overflow-auto max-h-48">{JSON.stringify(testResult.response, null, 2)}</pre>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => { setTestTarget(null); setTestResult(null); }}>{t('Close')}</Button>
            <Button onClick={runTest} disabled={testing} data-testid="run-int-test">
              <Send className={testing ? 'w-4 h-4 mr-1 animate-pulse' : 'w-4 h-4 mr-1'} />{t('Send test lead')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pullTarget} onOpenChange={(open) => { if (!open) { setPullTarget(null); setPullResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('Pull sync')}{pullTarget ? ' - ' + pullTarget.name : ''}</DialogTitle>
            <DialogDescription>{pullTarget && pullTarget.type === 'openaff' ? t('Pull lead statuses from OpenAFF for the number of days set on the integration and update stored leads. Set the status API token on the integration first.') : t('Pull lead statuses and deposits from TrackBox (last 7 days) and update stored leads. Set the Pull x-api-key on the integration first.')}</DialogDescription>
          </DialogHeader>
          {pullResult ? (
            <div className="space-y-2 text-sm">
              {pullResult.error ? <div className="text-destructive break-all">{pullResult.error}</div> : (
                <div className="flex flex-wrap gap-3">
                  <span><span className="text-muted-foreground">{t('Fetched')}: </span>{pullResult.fetched != null ? pullResult.fetched : 0}</span>
                  <span><span className="text-muted-foreground">{t('Matched')}: </span>{pullResult.matched != null ? pullResult.matched : 0}</span>
                  <span><span className="text-muted-foreground">{t('Updated')}: </span>{pullResult.updated != null ? pullResult.updated : 0}</span>
                </div>
              )}
              {pullResult.by_status && Object.keys(pullResult.by_status).length ? (
                <div className="text-xs text-muted-foreground">{JSON.stringify(pullResult.by_status)}</div>
              ) : null}
              {pullResult.raw_sample && pullResult.raw_sample.length ? (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{t('Response sample')}</div>
                  <pre className="bg-surface-2 p-2 rounded text-xs overflow-auto max-h-48">{JSON.stringify(pullResult.raw_sample, null, 2)}</pre>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t('Runs a rolling 7-day sync. Use this to verify the response before enabling auto pull-sync.')}</p>
          )}
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => { setPullTarget(null); setPullResult(null); }}>{t('Close')}</Button>
            <Button onClick={runPull} disabled={pulling} data-testid="run-int-pull">
              <RefreshCw className={pulling ? 'w-4 h-4 mr-1 animate-spin' : 'w-4 h-4 mr-1'} />{t('Run pull sync')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteIntTarget}
        onOpenChange={(open) => { if (!open) setDeleteIntTarget(null); }}
        title={t('Delete integration')}
        description={t('This will permanently delete "{name}".').replace('{name}', deleteIntTarget?.name || '')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDeleteInt(deleteIntTarget?.id)}
      />
      <ConfirmDialog
        open={!!deleteWHTarget}
        onOpenChange={(open) => { if (!open) setDeleteWHTarget(null); }}
        title={t('Delete webhook')}
        description={t('This will permanently delete webhook "{name}". Events will no longer be forwarded.').replace('{name}', deleteWHTarget?.name || '')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDeleteWH(deleteWHTarget?.id)}
      />
    </div>
  );
}
